import { createSelector } from 'reselect';
import {
    map, filter, has, pick, values, isEmpty, get, groupBy, find,
} from 'lodash';
import { CONSTANTS } from 'services/constants';
import { buildRequestByReservationIdSelector } from './requests.selector';

/**
 * Base Selectors
 */
const getEntityId = (state, id) => id;
const getEntityIds = (state, ids) => ids;
const getEntities = (state) => state.app.database.members;
const getMetaEntities = (state) => get(state, 'app.metaDatabase.members', {});
const getMemberCreditCardDescriptions = (state) => get(state, 'app.database.memberCreditCardDescriptions', {});
const getMemberEmails = (state) => get(state, 'app.database.memberEmails', {});
const getMemberPhones = (state) => get(state, 'app.database.memberPhones', {});
const getTravelerPreferences = (state) => state.app.database.travelerPreferences;
const getReservationMembers = (state) => state.app.database.reservationMembers;
const getAuth = (state) => get(state, 'app.auth', {});
const getAuthUser = (state) => get(state, 'app.database.authUser', {});

/**
 * Selector Builders
 */
export const buildMetaMemberSelector = () => createSelector([getEntityId, getMetaEntities], (entityId, metaEntities) =>
{
    if (has(metaEntities, entityId))
    {
        return metaEntities[entityId];
    }

    return {};
});

export const buildMemberCreditCardDescriptionsSelector = () => createSelector([getEntityId, getMemberCreditCardDescriptions], (entityId, membersCreditCardDescriptions) =>
{
    const groupedMemberCreditCardDescriptions = groupBy(membersCreditCardDescriptions, 'member_id');
    return get(groupedMemberCreditCardDescriptions, entityId, []);
});

export const buildCurrentMemberSelector = () => createSelector([getAuth, getAuthUser], (authState, authUsers) =>
{
    if (authState.user.id && has(authUsers, authState.user.id))
    {
        const { member } = authUsers[authState.user.id];
        const defaultEmail = get(find(member.memberEmails, { default: 1 }), 'email', '');
        const defaultPhone = get(find(member.memberPhones, { default: 1 }), 'display_phone', '');

        return {
            ...member,
            defaultEmail,
            defaultPhone,
        };
    }
    return null;
});

export const buildMemberSelector = () => createSelector([getEntityId, getEntities, getMemberEmails, getMemberPhones, getTravelerPreferences], (entityId, entities, membersEmails, membersPhones, travelerPreferences) =>
{
    if (has(entities, entityId))
    {
        const member = get(entities, entityId);
        const filteredMemberEmails = filter(membersEmails, { member_id: entityId });
        const filteredMemberPhones = filter(membersPhones, { member_id: entityId });
        const filteredPreferences = filter(travelerPreferences, { member_id: entityId });

        const defaultEmail = get(find(filteredMemberEmails, { default: 1 }), 'email', '');
        const defaultPhone = get(find(filteredMemberPhones, { default: 1 }), 'display_phone', '');

        return {
            ...member,
            memberEmails: filteredMemberEmails,
            memberPhones: filteredMemberPhones,
            defaultEmail,
            defaultPhone,
            travelerPreferences: filteredPreferences,
        };
    }

    return null;
});

export const buildMemberByReservationIdSelector = () =>
{
    const getRequestByReservationIdSelector = buildRequestByReservationIdSelector();

    return createSelector([getRequestByReservationIdSelector, getEntities], (request, members) =>
    {
        if (has(request, 'member_id') && has(members, request.member_id))
        {
            return members[request.member_id];
        }

        return {};
    });
};

export const buildPreviousProfilesByMemberIdSelector = () =>
{
    const getMemberSelector = buildMemberSelector();

    return createSelector([getMemberSelector, getEntities], (entity, entities) =>
    {
        if (!isEmpty(entity) && has(entity, 'previousProfiles') && entity.previousProfiles.length > 0)
        {
            const response = [];

            entity.previousProfiles.forEach((v) =>
            {
                if (has(entities, v)) response.push(entities[v]);
            });

            return response;
        }

        return [];
    });
};

export const buildReservationMembersByReservationIdSelector = () =>
{
    const reservationMemberSelector = buildMemberByReservationIdSelector();

    return createSelector([getEntityId, reservationMemberSelector, getReservationMembers, getEntities, getMemberCreditCardDescriptions, getMemberEmails, getMemberPhones], (reservationId, reservationMember, reservationMembers, members, membersCreditCardDescriptions, membersEmails, membersPhones) =>
    {
        const memberIds = map(filter(reservationMembers, { reservation_id: reservationId }), 'member_id');

        let finalMembers = pick(members, memberIds);

        if (!reservationMember.is_travel_agent)
        {
            finalMembers = {
                [reservationMember.id]: reservationMember,
                ...finalMembers,
            };
        }

        // Group member credit card descriptions by member id
        const groupedMemberCreditCardDescriptions = groupBy(membersCreditCardDescriptions, 'member_id');

        // Add member credit card descriptions to each member
        const finalTravelers = map(finalMembers, (member) =>
        {
            const memberCreditCardDescriptions = get(groupedMemberCreditCardDescriptions, member.id, []);
            const memberEmails = filter(membersEmails, { member_id: member.id });
            const memberPhones = filter(membersPhones, { member_id: member.id });
            return {
                ...member,
                memberEmails,
                memberPhones,
                memberCreditCardDescriptions,
            };
        });

        return values(finalTravelers);
    });
};

export const buildMembersByIdsSelector = () => createSelector([getEntityIds, getEntities], (membersIds, members) => values(pick(members, membersIds)));

export const buildMemberTravelersSelector = () => createSelector([getEntityId, getEntities, getMemberEmails], (entityId, entities) =>
{
    if (has(entities, entityId))
    {
        return filter(entities, (member) => member.parent_id === entityId || member.id === entityId);
    }

    return [];
});
